/**
 * Shared data shapes for the frontend and the backend.
 * Every shape is a zod schema (for runtime validation) plus its inferred TypeScript type.
 * Dates are ISO strings: "YYYY-MM-DD" for due dates, full ISO 8601 for timestamps.
 */
import { z } from "zod";

// ─── Enums ──────────────────────────────────────────────────────────────────

/** import = pasted text or screenshots from a school website, read by the AI (Mike, 2026-10-01). */
export const SourceSchema = z.enum(["google_classroom", "infinite_campus", "manual", "import"]);
export type Source = z.infer<typeof SourceSchema>;

export const ItemTypeSchema = z.enum([
  "homework",
  "test",
  "project",
  "reading",
  "form",
  "bring_item",
  "event",
  "schedule_change",
  "reminder",
  // Added for things students add themselves (Mike, 2026-09-30).
  "meeting",
  "practice",
  "game",
]);
export type ItemType = z.infer<typeof ItemTypeSchema>;

export const ImpactSchema = z.enum(["high", "medium", "low"]);
export type Impact = z.infer<typeof ImpactSchema>;

/** explicit = date written in the post; relative = resolved from the posted date; none = "needs a date" tray. */
export const DateConfidenceSchema = z.enum(["explicit", "relative", "none"]);
export type DateConfidence = z.infer<typeof DateConfidenceSchema>;

export const ItemStatusSchema = z.enum(["not_started", "in_progress", "done"]);
export type ItemStatus = z.infer<typeof ItemStatusSchema>;

export const CreatedBySchema = z.enum(["source", "ai", "student"]);
export type CreatedBy = z.infer<typeof CreatedBySchema>;

export const PostTypeSchema = z.enum(["assignment", "announcement", "material", "school_notice"]);
export type PostType = z.infer<typeof PostTypeSchema>;

/** Under-13 handling is an open decision (school authorization vs parent consent). */
export const AgeGateStatusSchema = z.enum([
  "not_checked",
  "13_or_older",
  "under_13_pending",
  "under_13_approved",
]);
export type AgeGateStatus = z.infer<typeof AgeGateStatusSchema>;

export const ConnectionStatusSchema = z.enum(["connected", "needs_attention", "not_connected"]);
export type ConnectionStatus = z.infer<typeof ConnectionStatusSchema>;

export const AuditEventSchema = z.enum(["login", "connect", "disconnect", "delete", "ai_run", "parent_consent"]);
export type AuditEvent = z.infer<typeof AuditEventSchema>;

// ─── Building blocks ────────────────────────────────────────────────────────

export const IsoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD");
export const TimeSchema = z.string().regex(/^\d{2}:\d{2}$/, "Expected HH:MM");
export const TimestampSchema = z.iso.datetime({ offset: true });

export const LinkSchema = z.object({
  title: z.string(),
  url: z.url(),
});
export type Link = z.infer<typeof LinkSchema>;

/** Where an Item came from. One Item can have several sources (assignment + announcement + reminder). */
export const ItemSourceRefSchema = z.object({
  source: SourceSchema,
  postId: z.string().nullable(),
  url: z.url().nullable(),
});
export type ItemSourceRef = z.infer<typeof ItemSourceRefSchema>;

// ─── Firestore documents ────────────────────────────────────────────────────

/** students/{uid} */
export const StudentProfileSchema = z.object({
  displayName: z.string(),
  schoolEmail: z.email(),
  school: z.string(),
  grade: z.number().int().min(6).max(12),
  clubs: z.array(z.string()),
  sports: z.array(z.string()),
  interests: z.array(z.string()),
  /** Optional for everyone; required for students under 13 (parent consent). */
  parentEmail: z.email().nullable(),
  /** A second parent or guardian, optional (Mike, 2026-09-30). Same access as the first. */
  secondParentEmail: z.email().nullable().default(null),
  ageGateStatus: AgeGateStatusSchema,
  createdAt: TimestampSchema,
});
export type StudentProfile = z.infer<typeof StudentProfileSchema>;

const TagListSchema = z.array(z.string().trim().min(1).max(40)).max(20);

/** Fields a student can edit on the Profile page. */
export const ProfileUpdateSchema = z.object({
  displayName: z.string().trim().min(1).max(80),
  schoolEmail: z.email(),
  school: z.string().trim().min(1).max(120),
  grade: z.number().int().min(6).max(12),
  clubs: TagListSchema,
  sports: TagListSchema,
  interests: TagListSchema,
  parentEmail: z.email().nullable(),
  secondParentEmail: z.email().nullable().optional(),
});
export type ProfileUpdate = z.infer<typeof ProfileUpdateSchema>;

/**
 * First-time onboarding. Birth month and year are used once to work out the age
 * check, then thrown away: they are never stored.
 */
export const OnboardingInputSchema = ProfileUpdateSchema.extend({
  /** Only asked when the year alone can't tell 12 from 13 (`needsBirthMonth`). */
  birthMonth: z.number().int().min(1).max(12).nullable().default(null),
  birthYear: z.number().int().min(1990).max(2030),
});
export type OnboardingInput = z.infer<typeof OnboardingInputSchema>;

/** A student account that lists the signed-in person's email as its parent email. */
export const LinkedStudentSchema = z.object({
  uid: z.string(),
  displayName: z.string(),
  ageGateStatus: AgeGateStatusSchema,
});
export type LinkedStudent = z.infer<typeof LinkedStudentSchema>;

/** GET /api/me: the account being viewed, and who is viewing it. */
export const MeResponseSchema = z.object({
  uid: z.string(),
  profile: StudentProfileSchema.nullable(),
  /** "parent" when a parent is viewing a student's account. */
  viewer: z.enum(["student", "parent"]),
  /** Students whose parent email matches the signed-in (verified) Google email. */
  linkedStudents: z.array(LinkedStudentSchema),
});
export type MeResponse = z.infer<typeof MeResponseSchema>;

/** students/{uid}/connections/{source} */
export const ConnectionSchema = z.object({
  source: SourceSchema,
  status: ConnectionStatusSchema,
  lastSyncAt: TimestampSchema.nullable(),
  lastError: z.string().nullable(),
  /** Pointer to the Secret Manager entry. Never the token itself. */
  tokenRef: z.string().nullable(),
  /** Set once the AI organizer has read this source's whole school year (the one-time catch-up). */
  aiBackfilled: z.boolean().optional(),
  /** Last time the whole school year was read, which is when things deleted in the source are removed. */
  lastFullSyncAt: TimestampSchema.nullable().optional(),
  /** Every class the source has (from the last sync), so Settings can list them, including ones turned off. */
  courses: z.array(z.object({ id: z.string(), name: z.string() })).optional(),
  /** Class IDs the student turned off in Settings: never synced, their source data removed (Mike, 2026-10-15). */
  skippedClassIds: z.array(z.string()).optional(),
  /** Nothing posted before this is read: 2 weeks before the first sync (Mike, 2026-10-16), so a May sign-up skips the year. */
  historyFrom: TimestampSchema.optional(),
});
export type Connection = z.infer<typeof ConnectionSchema>;

/** students/{uid}/classes/{classId} */
export const ClassSchema = z.object({
  id: z.string(),
  name: z.string(),
  source: SourceSchema,
  sourceId: z.string(),
  color: z.string().regex(/^#[0-9a-f]{6}$/i),
  /** Role only, for example "science teacher". Never a name. */
  teacherRoleLabel: z.string(),
  /**
   * class = a school class (from a source, or added on My Classes); club and sport come from the
   * student's Profile tags (Mike, 2026-09-30: clubs, sports and the schedule fill the class lists).
   */
  kind: z.enum(["class", "club", "sport"]).default("class"),
  /** Optional, set by the student on My Classes, for example "3" or "A". */
  period: z.string().max(20).nullable().default(null),
  /** Optional, set by the student on My Classes, for example "214". */
  room: z.string().max(40).nullable().default(null),
});
export type Class = z.infer<typeof ClassSchema>;
export type ClassKind = Class["kind"];

/** students/{uid}/items/{itemId}: the one shared record behind Checklist, Calendar and Assignments. */
export const ItemSchema = z.object({
  id: z.string(),
  type: ItemTypeSchema,
  title: z.string(),
  classId: z.string().nullable(),
  due: IsoDateSchema.nullable(),
  dueTime: TimeSchema.nullable(),
  dateConfidence: DateConfidenceSchema,
  summary: z.string(),
  steps: z.array(z.string()),
  status: ItemStatusSchema,
  done: z.boolean(),
  impact: ImpactSchema,
  pinned: z.boolean(),
  snoozedUntil: IsoDateSchema.nullable(),
  sources: z.array(ItemSourceRefSchema),
  relatedLinks: z.array(LinkSchema),
  dateChangedFrom: IsoDateSchema.nullable(),
  /**
   * The due date the source system last reported (assignments only). Sync changes `due` only when
   * this changes, so a date moved by a teacher's announcement isn't undone by the next sync.
   */
  sourceDue: IsoDateSchema.nullable(),
  createdBy: CreatedBySchema,
  notes: z.string(),
  /** Hidden by the student ("Remove"). School items are hidden, not deleted, so the next sync can't bring them back. */
  removed: z.boolean().default(false),
  /** Fields the student changed on a school item. Sync keeps these (except a due date the teacher moves). */
  studentEdited: z.array(z.enum(["title", "type", "classId", "due"])).default([]),
  /** Items created together by "Repeats every week" share this ID. Null for one-off items. */
  seriesId: z.string().nullable().default(null),
});
export type Item = z.infer<typeof ItemSchema>;

/** students/{uid}/posts/{postId}: announcement feed entry. Stores the AI summary, never the original text. */
export const PostSchema = z.object({
  id: z.string(),
  source: SourceSchema,
  classId: z.string().nullable(),
  postedAt: TimestampSchema,
  /** Link back to the post. Null for imported announcements (there's nothing to link to). */
  sourceUrl: z.url().nullable(),
  /** Empty until the AI organizer has read the post (organizerStatus "pending"). */
  aiSummary: z.string(),
  organizerStatus: z.enum(["pending", "done", "needs_review"]),
  impact: ImpactSchema,
  impactOverride: ImpactSchema.nullable(),
  read: z.boolean(),
  pinned: z.boolean(),
  linkedItemIds: z.array(z.string()),
  /** The student moved it to another class (Mike, 2026-10-06). Sync keeps their choice. */
  classEdited: z.boolean().default(false),
});
export type Post = z.infer<typeof PostSchema>;

/** students/{uid}/manualInputs/{id}: Add Homework submissions, kept 24 h after processing. */
export const ManualInputSchema = z.object({
  kind: z.enum(["text", "photo", "url"]),
  status: z.enum(["pending", "processing", "needs_confirm", "done", "failed"]),
  storagePath: z.string().nullable(),
  resultItemId: z.string().nullable(),
  createdAt: TimestampSchema,
});
export type ManualInput = z.infer<typeof ManualInputSchema>;

/** auditLog/{id}: hashed IDs and event names only. */
export const AuditLogEntrySchema = z.object({
  timestamp: TimestampSchema,
  hashedUid: z.string().regex(/^[0-9a-f]{64}$/),
  event: AuditEventSchema,
  success: z.boolean(),
});
export type AuditLogEntry = z.infer<typeof AuditLogEntrySchema>;

// ─── Organizer pipeline ─────────────────────────────────────────────────────

/** A post from any source, normalized. Lives in memory only; the original text is never stored. */
export const SourcePostSchema = z.object({
  source: SourceSchema,
  sourceId: z.string(),
  classId: z.string().nullable(),
  postType: PostTypeSchema,
  postedAt: TimestampSchema,
  title: z.string().nullable(),
  text: z.string(),
  attachments: z.array(LinkSchema),
  sourceUrl: z.url().nullable(),
  /** Due date the source system itself provides (for example a Classroom assignment). */
  sourceDue: z.object({ date: IsoDateSchema, time: TimeSchema.nullable() }).nullable(),
  /** When the post was last edited in the source (decides whether the AI reads it again). */
  updatedAt: TimestampSchema.optional(),
});
export type SourcePost = z.infer<typeof SourcePostSchema>;

export const OrganizerTaskSchema = z.object({
  type: ItemTypeSchema,
  title: z.string(),
  classId: z.string().nullable(),
  due: IsoDateSchema.nullable(),
  dueTime: TimeSchema.nullable(),
  dateConfidence: DateConfidenceSchema,
  summary: z.string(),
  steps: z.array(z.string()),
  studyReminderDaysBefore: z.number().int().min(0).nullable(),
  relatedLinks: z.array(z.string()),
  updatesExisting: z.boolean(),
});
export type OrganizerTask = z.infer<typeof OrganizerTaskSchema>;

/** The exact JSON shape the AI model must return. */
export const OrganizerResultSchema = z.object({
  postSummary: z.string(),
  impact: ImpactSchema,
  impactReason: z.string(),
  tasks: z.array(OrganizerTaskSchema),
});
export type OrganizerResult = z.infer<typeof OrganizerResultSchema>;

// ─── Settings: connected sources ────────────────────────────────────────────

/** GET /api/connections: one entry per source the app supports. */
export const ConnectionsResponseSchema = z.object({
  /** "mock" when the server runs with MOCK_MODE (sample data, no real Google sign-in). */
  mode: z.enum(["mock", "live"]),
  connections: z.array(
    z.object({
      source: SourceSchema,
      status: ConnectionStatusSchema,
      lastSyncAt: TimestampSchema.nullable(),
      lastError: z.string().nullable(),
      /** The source's classes, A to Z. `included: false` = the student turned it off. Empty before the first sync. */
      classes: z.array(z.object({ id: z.string(), name: z.string(), included: z.boolean() })).default([]),
    }),
  ),
});
export type ConnectionsResponse = z.infer<typeof ConnectionsResponseSchema>;

/** Settings → Choose classes: turn one class's sync on or off. */
export const ConnectionClassUpdateSchema = z.object({
  classId: z.string().min(1).max(300),
  included: z.boolean(),
});
export type ConnectionClassUpdate = z.infer<typeof ConnectionClassUpdateSchema>;

/** POST /api/connections/:source/start */
export const ConnectStartResponseSchema = z.object({
  /** Where to send the browser for the source's own permission screen. Null in mock mode (already connected). */
  authUrl: z.url().nullable(),
});

/** POST /api/connections/:source/sync */
export const SyncResultSchema = z.object({
  classes: z.number().int(),
  items: z.number().int(),
  posts: z.number().int(),
  syncedAt: TimestampSchema,
});
export type SyncResult = z.infer<typeof SyncResultSchema>;

// ─── Checklist, Calendar, Assignments, Announcements ────────────────────────

/** GET /api/schoolwork: everything the four main pages show, from one request. */
export const SchoolworkResponseSchema = z.object({
  classes: z.array(ClassSchema),
  items: z.array(ItemSchema),
  posts: z.array(PostSchema),
});
export type SchoolworkResponse = z.infer<typeof SchoolworkResponseSchema>;

const nonEmpty = (o: object) => Object.keys(o).length > 0;

/**
 * PATCH /api/items/:id. Only the fields a student may change; anything else is rejected.
 * Students may edit and remove any item (Mike, 2026-09-30).
 */
export const ItemUpdateSchema = z
  .strictObject({
    done: z.boolean(),
    status: ItemStatusSchema,
    pinned: z.boolean(),
    snoozedUntil: IsoDateSchema.nullable(),
    notes: z.string().max(2000),
    title: z.string().trim().min(1).max(200),
    type: ItemTypeSchema,
    classId: z.string().nullable(),
    due: IsoDateSchema.nullable(),
    dueTime: TimeSchema.nullable(),
    removed: z.boolean(),
  })
  .partial()
  .refine(nonEmpty, "Nothing to change");
export type ItemUpdate = z.infer<typeof ItemUpdateSchema>;

/** Most weeks one "Repeats every week" can add (about a school year). */
export const MAX_REPEATS = 52;

/** POST /api/items: something the student adds themselves. */
export const ItemCreateSchema = z
  .strictObject({
    title: z.string().trim().min(1).max(200),
    type: ItemTypeSchema,
    classId: z.string().nullable(),
    due: IsoDateSchema.nullable(),
    dueTime: TimeSchema.nullable(),
    notes: z.string().max(2000),
    /** "Repeats every week" until this date (inclusive). Needs a due date on or before it. */
    repeatWeeklyUntil: IsoDateSchema.nullable().optional(),
  })
  .refine((i) => !i.repeatWeeklyUntil || (i.due !== null && i.repeatWeeklyUntil >= i.due), {
    message: "The end date must be on or after the first date.",
  })
  .refine(
    (i) => !i.repeatWeeklyUntil || !i.due || Date.parse(i.repeatWeeklyUntil) - Date.parse(i.due) < MAX_REPEATS * 7 * 86_400_000,
    { message: "Repeats can go up to 52 weeks. Pick an earlier end date." },
  );
export type ItemCreate = z.infer<typeof ItemCreateSchema>;

/** POST /api/items: one item, or one per week for a repeating item. */
export const ItemCreateResponseSchema = z.object({ items: z.array(ItemSchema) });


/** PATCH /api/posts/:id. Only the fields a student may change; anything else is rejected. */
export const PostUpdateSchema = z
  .strictObject({
    read: z.boolean(),
    pinned: z.boolean(),
    impactOverride: ImpactSchema.nullable(),
    /** Move the announcement to another class (null = school-wide). */
    classId: z.string().nullable(),
  })
  .partial()
  .refine(nonEmpty, "Nothing to change");
export type PostUpdate = z.infer<typeof PostUpdateSchema>;

// ─── My Classes ────────────────────────────────────────────────────────────

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullable()
    .transform((v) => v || null);

/** POST /api/classes: a class the student types in (clubs and sports come from the Profile). */
export const ClassCreateSchema = z.strictObject({
  name: z.string().trim().min(1).max(80),
  period: optionalText(20),
  room: optionalText(40),
});
export type ClassCreate = z.infer<typeof ClassCreateSchema>;

/** PATCH /api/classes/:id. Only classes the student added can be renamed. */
export const ClassUpdateSchema = z
  .strictObject({
    name: z.string().trim().min(1).max(80),
    period: optionalText(20),
    room: optionalText(40),
  })
  .partial()
  .refine(nonEmpty, "Nothing to change");
export type ClassUpdate = z.infer<typeof ClassUpdateSchema>;

// ─── Live messages ──────────────────────────────────────────────────────────

/**
 * GET /api/messages: the original text of the student's recent announcements and materials,
 * fetched live from the school system for this screen only. Never stored (data rule: no post text).
 */
export const MessagesResponseSchema = z.object({
  /** Keyed by Post id. */
  messages: z.record(
    z.string(),
    z.object({
      title: z.string().nullable(),
      text: z.string(),
      attachments: z.array(LinkSchema),
    }),
  ),
  /** Sources that couldn't be reached right now. */
  failed: z.array(SourceSchema),
});
export type MessagesResponse = z.infer<typeof MessagesResponseSchema>;

// ─── Import (paste or screenshots) ──────────────────────────────────────────

/** Longest single paste or screenshot text the AI reads in one go. */
export const IMPORT_PART_MAX_CHARS = 12_000;
/** Most pastes and screenshots in one import. */
export const IMPORT_MAX_PARTS = 10;

/**
 * POST /api/imports: text the student copied from a school website, or read from screenshots on their
 * device. Never stored: only the AI's findings are kept, as a draft the student reviews.
 */
export const ImportRequestSchema = z.strictObject({
  parts: z
    .array(z.string().trim().min(1).max(IMPORT_PART_MAX_CHARS))
    .min(1)
    .max(IMPORT_MAX_PARTS),
  from: z.enum(["paste", "screenshots"]),
});
export type ImportRequest = z.infer<typeof ImportRequestSchema>;

/** One task the AI found, with the class name as the text showed it. */
export const ImportTaskSchema = OrganizerTaskSchema.extend({
  className: z.string().nullable(),
  impact: ImpactSchema,
  /** The item already in Dueable that this matches, if any (unticked in the review unless it changes it). */
  existingItemId: z.string().nullable(),
});
export type ImportTask = z.infer<typeof ImportTaskSchema>;

export const ImportAnnouncementSchema = z.object({
  className: z.string().nullable(),
  postedOn: IsoDateSchema.nullable(),
  summary: z.string(),
  impact: ImpactSchema,
  /** A very similar announcement was already imported. */
  duplicate: z.boolean(),
});
export type ImportAnnouncement = z.infer<typeof ImportAnnouncementSchema>;

/** students/{uid}/imports/{id}: what the AI found, waiting for the student's review. Deleted once added; expires in 24 h. */
export const ImportDraftSchema = z.object({
  id: z.string(),
  createdAt: TimestampSchema,
  /** Every class name the AI saw, and the student's class it matches (null = not in their list yet). */
  classes: z.array(z.object({ name: z.string(), classId: z.string().nullable() })),
  tasks: z.array(ImportTaskSchema),
  announcements: z.array(ImportAnnouncementSchema),
  /** Pastes or screenshots the AI couldn't read (shown so nothing is silently missed). */
  failedParts: z.number().int(),
});
export type ImportDraft = z.infer<typeof ImportDraftSchema>;

/** POST /api/imports/:id/confirm: which findings to add, and where each class name goes. */
export const ImportConfirmSchema = z.strictObject({
  tasks: z.array(z.number().int().min(0)).max(500),
  announcements: z.array(z.number().int().min(0)).max(500),
  /**
   * Class name → one of the student's class IDs, { newName } to add a class with that name to My Classes
   * (Mike, 2026-10-06: the student can name it), or null for no class. The key "" is for things the AI
   * couldn't place in a class.
   */
  classes: z.record(
    z.string(),
    z.union([z.string(), z.null(), z.strictObject({ newName: z.string().trim().min(1).max(80) })]),
  ),
});
export type ImportConfirm = z.infer<typeof ImportConfirmSchema>;

export const ImportConfirmResponseSchema = z.object({
  added: z.number().int(),
  updated: z.number().int(),
  announcements: z.number().int(),
  classesAdded: z.number().int(),
});
export type ImportConfirmResponse = z.infer<typeof ImportConfirmResponseSchema>;

// ─── Contact form ───────────────────────────────────────────────────────────

export const CONTACT_TOPICS = ["question", "privacy", "school", "problem", "other"] as const;

/** POST /api/contact: the public contact form (Mike, 2026-10-13: a form instead of an email link). */
export const ContactRequestSchema = z.strictObject({
  name: z.string().trim().max(80),
  email: z.email().max(200),
  topic: z.enum(CONTACT_TOPICS),
  message: z.string().trim().min(10).max(3000),
  /** Hidden from people; a bot that fills it in is ignored. */
  website: z.string().max(200).optional(),
});
export type ContactRequest = z.infer<typeof ContactRequestSchema>;

export const ContactResponseSchema = z.object({ ok: z.literal(true) });
